import { useCallback, useEffect, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import {
  Box, Card, CardContent, Typography, TextField, Button, Stack, Alert, CircularProgress,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { card, btn, typo, inputSx, layout } from '../styles/ds';
import { websiteRequest } from './websiteApi';

/**
 * Website settings (R1, WD-018): public contact details and pickup info shown
 * on the website, plus the private Zelle instructions, which the website never
 * shows — they are sent only with the payment instructions after the admin
 * approves an order's final amount. Also the order-value review threshold
 * (internal, never shown on the website).
 */
const FIELDS = [
  { key: 'whatsapp_number', label: 'מספר וואטסאפ של העסק', dir: 'ltr',
    help: 'ספרות בלבד בפורמט בינלאומי, בלי + ובלי 0 בהתחלה (לדוגמה 972501234567). משמש לכפתור הוואטסאפ באתר.' },
  { key: 'support_email', label: 'אימייל לפניות לקוחות', dir: 'ltr', help: 'מוצג באתר.' },
  { key: 'support_phone', label: 'טלפון לפניות (רשות)', dir: 'ltr', help: 'מוצג באתר אם מולא.' },
  { key: 'pickup_location', label: 'מיקום לאיסוף עצמי', multiline: true, dir: 'ltr',
    help: 'מוצג בקופה כשהלקוח בוחר איסוף עצמי (באנגלית). כשריק, האתר לא מציג מיקום.' },
  { key: 'pickup_instructions', label: 'הוראות לאיסוף עצמי', multiline: true, dir: 'ltr', help: 'מוצג בקופה (באנגלית).' },
];

const WebsiteSettingsPage = () => {
  const notify = useNotify();
  const [loaded, setLoaded] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    websiteRequest('/website_settings')
      .then(r => { setLoaded(r); setValues(r.values); })
      .catch(err => notify(err.message, { type: 'error' }));
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  const dirty = loaded && Object.keys(values).some(k => values[k] !== loaded.values[k]);
  const save = async () => {
    setSaving(true);
    try {
      const r = await websiteRequest('/website_settings', 'PUT', { values, expected_updated_at: loaded.updated_at });
      setLoaded(r); setValues(r.values);
      notify('הגדרות האתר נשמרו', { type: 'success' });
    } catch (err) { notify(err.message, { type: 'error' }); }
    finally { setSaving(false); }
  };

  if (!loaded) return <Box p={6} textAlign="center"><CircularProgress /></Box>;
  const field = f => (
    <TextField key={f.key} label={f.label} value={values[f.key] ?? ''} fullWidth multiline={!!f.multiline} minRows={f.multiline ? 3 : undefined}
      onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))} disabled={saving}
      helperText={f.help} inputProps={{ dir: f.dir }} sx={inputSx} />
  );

  return (
    <Box className="crm-page" sx={{ p: { xs: 1, sm: 3 } }}>
      <Title title="הגדרות אתר" />
      <Typography {...typo.pageTitle} mb={1}>הגדרות אתר</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        פרטי הקשר והאיסוף שמוצגים באתר הלקוחות. השינויים מופיעים באתר מיד אחרי השמירה.
      </Typography>

      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography sx={{ ...typo.sectionTitle, mb: 2 }}>מוצג באתר</Typography>
          <Box sx={layout.formGrid}>{FIELDS.map(field)}</Box>
        </CardContent>
      </Card>

      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <ReceiptLongIcon fontSize="small" color="action" />
            <Typography sx={typo.sectionTitle}>תקרת הזמנה (פנימי)</Typography>
          </Stack>
          <Alert severity="info" sx={{ mb: 2 }}>
            לא מוצג באתר. הזמנה שסכום המוצרים בה (לפני משלוח) גבוה מהתקרה לא נחסמת: היא מתקבלת ומסומנת "מעל התקרה" לבדיקה שלך, כמו כל הזמנה מהאתר שממתינה לאישור הסכום הסופי.
          </Alert>
          <Box sx={{ maxWidth: 320 }}>
            {field({ key: 'order_review_threshold_usd', label: 'תקרה בדולרים', dir: 'ltr',
              help: 'סכום שלם, לדוגמה 100000. כשריק — ‎$100,000.' })}
          </Box>
        </CardContent>
      </Card>

      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <LockIcon fontSize="small" color="action" />
            <Typography sx={typo.sectionTitle}>פרטי תשלום ב-Zelle (פרטי)</Typography>
          </Stack>
          <Alert severity="info" sx={{ mb: 2 }}>
            לא מוצג באתר. נשלח ללקוח רק יחד עם הוראות התשלום, אחרי שמנהל אישר את הסכום הסופי של ההזמנה.
          </Alert>
          {field({ key: 'zelle_instructions', label: 'הוראות תשלום ב-Zelle', multiline: true, dir: 'ltr', help: 'באנגלית — כפי שהלקוח יקבל אותן.' })}
        </CardContent>
      </Card>

      <Stack direction="row" spacing={1.5}>
        <Button variant="contained" disabled={saving || !dirty} onClick={save} sx={btn.primary}>שמירה</Button>
        <Button variant="outlined" disabled={saving || !dirty} onClick={() => setValues(loaded.values)} sx={btn.cancel}>ביטול שינויים</Button>
      </Stack>
    </Box>
  );
};
export default WebsiteSettingsPage;
